import { AxiosInstance } from 'axios';
import axiosClient from './axiosClientConfig';
import { awsService } from './aws';
import { formatSignDocumentPayload } from '@/lib/formatSignDocumentPayload';
import { HttpResponse } from 'interfaces/httpClient';
import {
	ActionRequired,
	ActionRequiredCriteriaResponse,
	PreAnalyzeFileResponse,
	ActionRequiredRelated,
	DocumentSettlement,
	FileType,
	FollowUAnswersPayload,
	FollowUpQuestions,
	RetainerData,
	RetainerPackage,
	SettlementPayload,
	SignDocument,
	SignDocumentDetails,
	UploadFile,
	FollowUp,
	SendFollowUpPayload,
	AddFollowUpPayload,
} from '@/interfaces';
import { SendMessageToAllPayload } from '@/interfaces/inbox';

class ActionRequiredService {
	constructor(private client: AxiosInstance) {}
	async getActionRequiredFiles(
		applicationId: number
	): Promise<HttpResponse<FileType[]>> {
		return this.client.get(`/action-required/files/${applicationId}`);
	}
	async getActionRequired(
		caseId?: string
	): Promise<HttpResponse<ActionRequired[]>> {
		return this.client.get(`/action-required/pending/${caseId}`);
	}
	async getAllActionRequired(): Promise<HttpResponse<ActionRequired[]>> {
		return this.client.get(`/action-required/all-action-required`, {
			params: { page: 1, limit: 100 },
		});
	}
	async getFollowUpQuestions(
		id: number
	): Promise<HttpResponse<FollowUpQuestions[]>> {
		return this.client.get(`/action-required/${id}/questions`);
	}
	async putFollowUpAnswers(
		data: FollowUAnswersPayload
	): Promise<HttpResponse<FollowUAnswersPayload[]>> {
		return this.client.put(`/action-required/questions/answer`, data);
	}
	async postUploadFile(data: UploadFile): Promise<HttpResponse<FileType[]>> {
		const formData = new FormData();
		formData.append('doc', data.doc);
		data.applicationId &&
			formData.append('applicationId', data.applicationId.toString());
		data.actionRequiredId &&
			formData.append('actionRequiredId', data.actionRequiredId.toString());
		data.fileName && formData.append('fileName', data.fileName);
		return this.client.post('/action-required/upload-file', formData, {
			headers: { 'Content-Type': 'multipart/form-data' },
		});
	}
	async putApprovalAction(
		id: number,
		is_approved: boolean
	): Promise<HttpResponse<FollowUAnswersPayload[]>> {
		return this.client.put(`/action-required/${id}/process`, { is_approved });
	}

	async resolveSettlement(
		applicationId: string,
		payload: SettlementPayload
	): Promise<HttpResponse<any>> {
		return this.client.post(
			`/action-required/${applicationId}/resolve-settlement`,
			{ ...payload }
		);
	}

	async getDocumentSettlement(
		applicationId: string
	): Promise<HttpResponse<DocumentSettlement>> {
		return this.client.get(
			`/action-required/${applicationId}/document-settlement`
		);
	}

	async deleteFile(
		actionRequiredId: number,
		fileId: number
	): Promise<HttpResponse<void>> {
		return this.client.delete(`/action-required/delete-file`, {
			data: { actionRequiredId, fileId },
		});
	}

	async signDocument(body: SignDocument): Promise<HttpResponse<void>> {
		let documentUrl: string | undefined;
		if (body.doc) {
			const file = new File(
				[body.doc],
				`signed-document-${body.action_id}.pdf`,
				{
					type: 'application/pdf',
				}
			);
			documentUrl = (await awsService.uploadImage(file)).data.data.location;
		}
		return this.client.post(
			`/action-required/sign-document-ar`,
			formatSignDocumentPayload(body, documentUrl)
		);
	}

	async getActionRequireReviewSign(
		actionId: string
	): Promise<HttpResponse<SignDocumentDetails>> {
		return this.client.get(`/action-required/review-sign/${actionId}`);
	}

	async getSignRetainerPackage(
		caseId: string
	): Promise<HttpResponse<RetainerPackage[]>> {
		return this.client.get(`/action-required/retainer-setup/${caseId}`);
	}

	async getActionRequiredRelated(
		id: number
	): Promise<HttpResponse<ActionRequiredRelated[]>> {
		return this.client.get(`/action-required/related/${id}`);
	}

	async getRetainerData(id: number): Promise<HttpResponse<RetainerData>> {
		return this.client.get(`/action-required/${id}/retainer-data`);
	}

	async actionRequiredPreAnalyzeFile(
		file: File,
		actionRequiredId: number,
		onUploadProgress?: (
			progressEvent: import('axios').AxiosProgressEvent
		) => void
	): Promise<HttpResponse<PreAnalyzeFileResponse>> {
		const formData = new FormData();
		formData.append('doc', file);
		formData.append('actionRequiredId', String(actionRequiredId));
		return this.client.post(`/action-required/pre-analyze-file`, formData, {
			onUploadProgress,
		});
	}

	async actionRequiredCriteria(
		actionRequiredId: number
	): Promise<HttpResponse<ActionRequiredCriteriaResponse>> {
		return this.client.get(`/action-required/${actionRequiredId}/criteria`);
	}

	async postSendFollowUp(
		payload: SendMessageToAllPayload
	): Promise<HttpResponse<{ message: string }>> {
		return this.client.post(`/action-required/send-follow-up`, payload);
	}

	async getFollowUp(id: number): Promise<HttpResponse<FollowUp>> {
		return this.client(`/action-required/follow-up/${id}`);
	}

	async sendFollowUp(id: number, data: SendFollowUpPayload) {
		return this.client.put(`/action-required/follow-up/${id}`, data);
	}

	async addFollowUp(data: AddFollowUpPayload) {
		return this.client.post(`/action-required/add-follow-up`, data);
	}

	async toggleStatusFollowUp(
		actionId: number,
		validate: boolean
	): Promise<HttpResponse<any>> {
		return this.client.post(`/action-required/complete/${actionId}`, {
			validate,
		});
	}

	async reviewSignatureDocument(
		actionId: number,
		validate: boolean
	): Promise<HttpResponse<any>> {
		return this.client.put(`/action-required/${actionId}/status`, {
			validate,
		});
	}

	async skipDocument(actionId: number): Promise<HttpResponse<any>> {
		return this.client.post(`/action-required/skip-document/${actionId}`);
	}

	async deleteSignVersion(id: number): Promise<HttpResponse<void>> {
		return this.client.delete(`/action-required/sign-version/${id}`);
	}

	async selectSignVersion(id: number): Promise<HttpResponse<void>> {
		return this.client.put(`action-required/sign-version/${id}/current`);
	}
}

export const actionRequiredService = new ActionRequiredService(axiosClient);
